import { AddIcon, SearchIcon, WarningTwoIcon } from "@chakra-ui/icons";
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  InputGroup,
  InputRightElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Switch,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React from "react";
import { auth } from "../../../core/auth";
import DebouncedInput from "../../../shared/components/DebouncedInput";
import Select from "../../../shared/components/Select";
import useApi from "../../../shared/hooks/useApi";
import { PlanOfCareTypeId, ServiceCodeId } from "../../../shared/schema/schema";
import { formatErrorResponse } from "../../../shared/utils/format-response-error";
import {
  emitRefreshPlansOfCare,
  filterPlanOfCareItems,
  getNextOrderInSection,
  getSections,
  PlanOfCareTypeItem,
  sectionOrderKey,
} from "../plan-of-care-type.utils";
import DeactivateItemDialog from "./DeactivateItemDialog";
import PlanOfCareDraftItemRow, { DraftItem, newDraftItem } from "./PlanOfCareDraftItemRow";

interface Props {
  officeName: string;
  planOfCareTypeId: PlanOfCareTypeId;
  showPatientSignature: boolean;
  onClose: () => void;
}

export default function PlanOfCareTypeItemsModal(props: Props) {
  const { queries, mutations } = useApi();
  const queryClient = useQueryClient();
  const toast = useToast();

  // Without the permission the catalogue stays readable - only the mutations are hidden.
  const canEdit = auth.isAllowedTo("edit_plan_of_care_items");

  const [searchTerm, setSearchTerm] = React.useState("");
  const [section, setSection] = React.useState<string | null>(null);
  const [showInactive, setShowInactive] = React.useState(false);
  const [drafts, setDrafts] = React.useState<DraftItem[]>([]);
  const [itemToDeactivate, setItemToDeactivate] = React.useState<PlanOfCareTypeItem | null>(null);
  const [showPatientSignature, setShowPatientSignature] = React.useState(
    props.showPatientSignature
  );

  const itemsQuery = useQuery(queries.planOfCareType.items(props.planOfCareTypeId));
  const items = React.useMemo(() => itemsQuery.data?.items ?? [], [itemsQuery.data]);
  const sections = React.useMemo(() => getSections(items), [items]);

  // The agency's service codes, for the per-item scope. Fetched once here rather
  // than per row. Inactive codes stay selectable so an item scoped to one keeps
  // showing what it is scoped to.
  const serviceCodesQuery = useQuery(queries.serviceCode.list());
  const serviceCodeOptions = React.useMemo(
    () =>
      (serviceCodesQuery.data?.serviceCodes ?? [])
        .slice()
        .sort((left, right) => left.code.localeCompare(right.code))
        .map((serviceCode) => ({
          value: serviceCode.id,
          label: serviceCode.isActive ? serviceCode.code : `${serviceCode.code} (inactive)`,
        })),
    [serviceCodesQuery.data]
  );

  const visibleItems = React.useMemo(
    () => filterPlanOfCareItems(items, { searchTerm, section, showInactive }),
    [items, searchTerm, section, showInactive]
  );

  const invalidateItems = () => {
    void queryClient.invalidateQueries({
      queryKey: queries.planOfCareType.items(props.planOfCareTypeId).queryKey,
    });
    void queryClient.invalidateQueries({ queryKey: queries.planOfCareType.settings().queryKey });
    emitRefreshPlansOfCare();
  };

  const showError = (title: string) => (error: unknown) =>
    toast({
      title,
      description: formatErrorResponse(error),
      status: "error",
      position: "top-right",
    });

  const addItemsMutation = useMutation({
    ...mutations.planOfCareType.addItems(props.planOfCareTypeId),
    onError: showError("Could not add tasks"),
    onSuccess: (response) => {
      setDrafts([]);
      invalidateItems();
      toast({
        title: `${response.items.length} task${response.items.length === 1 ? "" : "s"} added`,
        status: "success",
        position: "top-right",
      });
    },
  });

  const patchTypeMutation = useMutation({
    ...mutations.planOfCareType.patchType(props.planOfCareTypeId),
    onError: (error) => {
      setShowPatientSignature((value) => !value);
      showError("Could not update plan of care settings")(error);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queries.planOfCareType.settings().queryKey });
      emitRefreshPlansOfCare();
    },
  });

  const handleToggleSignature = (isChecked: boolean) => {
    setShowPatientSignature(isChecked);
    patchTypeMutation.mutate({ showPatientSignature: isChecked });
  };

  const handleAddDraft = () => {
    const defaultSection = section ?? sections[0] ?? "";
    setDrafts([
      ...drafts,
      newDraftItem({
        section: defaultSection,
        order: getNextOrderInSection(items, defaultSection) + drafts.length,
      }),
    ]);
  };

  const handleChangeDraft = (id: string, patch: Partial<DraftItem>) =>
    setDrafts(drafts.map((draft) => (draft.id === id ? { ...draft, ...patch } : draft)));

  const handleRemoveDraft = (id: string) => setDrafts(drafts.filter((draft) => draft.id !== id));

  const draftErrors = React.useMemo(() => validateDrafts({ drafts, items }), [drafts, items]);

  const handleSaveDrafts = () => {
    if (draftErrors.length > 0) {
      return;
    }
    addItemsMutation.mutate({
      items: drafts.map((draft) => ({
        code: draft.code.trim(),
        label: draft.label.trim(),
        section: draft.section.trim(),
        order: draft.order,
        isActive: true,
        isPersonalCare: draft.isPersonalCare,
        // Ignored by the server, which always writes "Task", but required by the message type.
        type: "Task" as const,
      })),
    });
  };

  return (
    <>
      <Modal isOpen={true} scrollBehavior="inside" size="6xl" onClose={props.onClose}>
        <ModalOverlay />
        <ModalContent maxH="90vh">
          <ModalHeader>
            <Text>{props.officeName}</Text>
            <Text color="gray.500" fontSize="sm" fontWeight="normal">
              Plan of care tasks
            </Text>
          </ModalHeader>
          <ModalCloseButton />

          <ModalBody>
            <VStack align="stretch" spacing={4}>
              <Alert borderRadius="md" status="info">
                <AlertIcon />
                <Text fontSize="sm">
                  {canEdit
                    ? "Task names and codes cannot be changed after creation - they are referenced by submitted plans of care, duty sheets and EVV code mappings. Add a new task and deactivate the old one instead."
                    : "You do not have permission to change plan of care tasks. Ask an administrator for the “Edit plan of care items” permission."}
                </Text>
              </Alert>

              <Flex align="center" gap={4} justify="space-between" wrap="wrap">
                <HStack spacing={3}>
                  <InputGroup width="xs">
                    <DebouncedInput
                      debounce={250}
                      placeholder="Search by task name or code"
                      value={searchTerm}
                      onChange={setSearchTerm}
                    />
                    <InputRightElement>
                      <SearchIcon color="gray.400" />
                    </InputRightElement>
                  </InputGroup>
                  <Select
                    allowUnselect={true}
                    label="Section"
                    multiple={false}
                    options={sections.map((value) => ({ label: value, value }))}
                    value={section}
                    onChange={(value) => setSection(value ?? null)}
                  />
                  <FormControl alignItems="center" display="flex" width="auto">
                    <FormLabel fontSize="sm" htmlFor="poc-show-inactive" mb={0}>
                      Show inactive
                    </FormLabel>
                    <Switch
                      id="poc-show-inactive"
                      isChecked={showInactive}
                      onChange={(e) => setShowInactive(e.target.checked)}
                    />
                  </FormControl>
                </HStack>

                <FormControl alignItems="center" display="flex" width="auto">
                  <FormLabel fontSize="sm" htmlFor="poc-patient-signature" mb={0}>
                    Show patient signature
                  </FormLabel>
                  <Switch
                    id="poc-patient-signature"
                    isChecked={showPatientSignature}
                    onChange={(e) => handleToggleSignature(e.target.checked)}
                  />
                </FormControl>
              </Flex>

              <Box borderColor="gray.200" borderRadius="md" borderWidth={1} overflowX="auto">
                <Table size="sm">
                  <Thead bg="gray.50" position="sticky" top={0} zIndex={1}>
                    <Tr>
                      <Th>Section</Th>
                      <Th isNumeric={true} width="80px">
                        Order
                      </Th>
                      <Th width="120px">Code</Th>
                      <Th>Task</Th>
                      <Th width="220px">
                        <Tooltip label="Visits billing a service code outside this list do not show the task. Empty means every service code.">
                          <Text as="span">Service codes</Text>
                        </Tooltip>
                      </Th>
                      <Th width="120px">Personal care</Th>
                      <Th width="140px">Status</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {visibleItems.map((item) => (
                      <ItemRow
                        key={item.id}
                        canEdit={canEdit}
                        isLoadingServiceCodes={serviceCodesQuery.isLoading}
                        item={item}
                        planOfCareTypeId={props.planOfCareTypeId}
                        serviceCodeOptions={serviceCodeOptions}
                        onChanged={invalidateItems}
                        onClickDeactivate={() => setItemToDeactivate(item)}
                      />
                    ))}
                    {drafts.map((draft) => (
                      <PlanOfCareDraftItemRow
                        key={draft.id}
                        draft={draft}
                        sections={sections}
                        onChange={(patch) => handleChangeDraft(draft.id, patch)}
                        onRemove={() => handleRemoveDraft(draft.id)}
                      />
                    ))}
                    {visibleItems.length === 0 && drafts.length === 0 && (
                      <Tr>
                        <Td colSpan={7}>
                          <Text color="gray.500" py={6} textAlign="center">
                            {itemsQuery.isLoading ? "Loading tasks..." : "No tasks match"}
                          </Text>
                        </Td>
                      </Tr>
                    )}
                  </Tbody>
                </Table>
              </Box>

              {canEdit && (
                <Box>
                  <Button
                    leftIcon={<AddIcon />}
                    size="sm"
                    variant="outline"
                    onClick={handleAddDraft}
                  >
                    Add task
                  </Button>
                </Box>
              )}

              {draftErrors.length > 0 && (
                <Alert borderRadius="md" status="error">
                  <AlertIcon />
                  <VStack align="start" spacing={0}>
                    {draftErrors.map((error) => (
                      <Text key={error} fontSize="sm">
                        {error}
                      </Text>
                    ))}
                  </VStack>
                </Alert>
              )}
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button mr={3} variant="ghost" onClick={props.onClose}>
              Close
            </Button>
            {canEdit && (
              <Button
                colorScheme="blue"
                isDisabled={drafts.length === 0 || draftErrors.length > 0}
                isLoading={addItemsMutation.isPending}
                onClick={handleSaveDrafts}
              >
                Save {drafts.length > 0 ? `${drafts.length} new task` : "tasks"}
                {drafts.length > 1 ? "s" : ""}
              </Button>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>

      {itemToDeactivate !== null && (
        <DeactivateItemDialog
          item={itemToDeactivate}
          planOfCareTypeId={props.planOfCareTypeId}
          onClose={() => setItemToDeactivate(null)}
          onDeactivated={invalidateItems}
        />
      )}
    </>
  );
}

function ItemRow(props: {
  canEdit: boolean;
  isLoadingServiceCodes: boolean;
  item: PlanOfCareTypeItem;
  planOfCareTypeId: PlanOfCareTypeId;
  serviceCodeOptions: { value: ServiceCodeId; label: string }[];
  onChanged: () => void;
  onClickDeactivate: () => void;
}) {
  const { mutations } = useApi();
  const toast = useToast();

  const setActiveMutation = useMutation({
    ...mutations.planOfCareType.setItemActive({
      planOfCareTypeId: props.planOfCareTypeId,
      planOfCareItemId: props.item.id,
    }),
    onError: (error) =>
      toast({
        title: "Could not reactivate task",
        description: formatErrorResponse(error),
        status: "error",
        position: "top-right",
      }),
    onSuccess: () => {
      props.onChanged();
      toast({ title: "Task reactivated", status: "success", position: "top-right" });
    },
  });

  const setServiceCodesMutation = useMutation({
    ...mutations.planOfCareType.setItemServiceCodes({
      planOfCareTypeId: props.planOfCareTypeId,
      planOfCareItemId: props.item.id,
    }),
    onError: (error) =>
      toast({
        title: "Could not update the task's service codes",
        description: formatErrorResponse(error),
        status: "error",
        position: "top-right",
      }),
    onSuccess: () => {
      props.onChanged();
    },
  });

  const serviceCodeLabel = (serviceCodeIds: ServiceCodeId[]) => {
    if (serviceCodeIds.length === 0) {
      return "All service codes";
    }
    return serviceCodeIds
      .map(
        (serviceCodeId) =>
          props.serviceCodeOptions.find((option) => option.value === serviceCodeId)?.label ??
          `#${serviceCodeId}`
      )
      .join(", ");
  };

  return (
    <Tr opacity={props.item.isActive ? 1 : 0.55}>
      <Td>{props.item.section}</Td>
      <Td isNumeric={true}>{props.item.orderInSection}</Td>
      <Td>
        <HStack spacing={1}>
          <Text>{props.item.code}</Text>
          {props.item.hasEvvMapping && (
            <Tooltip label="Mapped to an EVV task code">
              <WarningTwoIcon color="orange.400" />
            </Tooltip>
          )}
        </HStack>
      </Td>
      <Td>{props.item.label}</Td>
      <Td>
        {props.canEdit ? (
          <Select
            isLoading={props.isLoadingServiceCodes || setServiceCodesMutation.isPending}
            label="All service codes"
            multiple={true}
            options={props.serviceCodeOptions}
            searchable={true}
            selectedLabel={serviceCodeLabel}
            size="xs"
            value={props.item.serviceCodeIds}
            width="320px"
            onChange={(serviceCodeIds) =>
              setServiceCodesMutation.mutate({ serviceCodeIds: serviceCodeIds ?? [] })
            }
          />
        ) : (
          <Text fontSize="sm">{serviceCodeLabel(props.item.serviceCodeIds)}</Text>
        )}
      </Td>
      <Td>{props.item.isPersonalCare ? <Badge colorScheme="purple">Yes</Badge> : null}</Td>
      <Td>
        {!props.canEdit ? (
          <Badge colorScheme={props.item.isActive ? "green" : "gray"}>
            {props.item.isActive ? "Active" : "Inactive"}
          </Badge>
        ) : props.item.isActive ? (
          <Button size="xs" variant="outline" onClick={props.onClickDeactivate}>
            Deactivate
          </Button>
        ) : (
          <Button
            isLoading={setActiveMutation.isPending}
            size="xs"
            variant="outline"
            onClick={() => setActiveMutation.mutate({ isActive: true })}
          >
            Reactivate
          </Button>
        )}
      </Td>
    </Tr>
  );
}

function validateDrafts(params: { drafts: DraftItem[]; items: PlanOfCareTypeItem[] }): string[] {
  const errors: string[] = [];

  if (params.drafts.some((draft) => draft.label.trim() === "")) {
    errors.push("Every new task needs a name");
  }
  if (params.drafts.some((draft) => draft.code.trim() === "")) {
    errors.push("Every new task needs a code");
  }
  if (params.drafts.some((draft) => draft.section.trim() === "")) {
    errors.push("Every new task needs a section");
  }

  // Codes must be unique against inactive items too, since EVV mappings resolve items by code.
  const existingCodes = new Set(params.items.map((item) => item.code));
  const draftCodes = params.drafts.map((draft) => draft.code.trim()).filter((c) => c !== "");

  const clashing = draftCodes.filter((code) => existingCodes.has(code));
  if (clashing.length > 0) {
    errors.push(`Code already used in this plan of care: ${[...new Set(clashing)].join(", ")}`);
  }
  if (new Set(draftCodes).size !== draftCodes.length) {
    errors.push("The same code is used by more than one new task");
  }

  const existingSectionOrders = new Set(
    params.items.map((item) => sectionOrderKey(item.section, item.orderInSection))
  );
  const draftSectionOrders = params.drafts.map((draft) =>
    sectionOrderKey(draft.section.trim(), draft.order)
  );
  if (draftSectionOrders.some((key) => existingSectionOrders.has(key))) {
    errors.push("A new task reuses an order number already taken in that section");
  }
  if (new Set(draftSectionOrders).size !== draftSectionOrders.length) {
    errors.push("Two new tasks share an order number in the same section");
  }

  return errors;
}
